import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';

interface Chip {
  label: string;
  onRemove: () => void;
}

function Chip({ label, onRemove }: Chip) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-sd-muted border border-sd-border px-2.5 py-0.5 text-xs font-medium text-sd-foreground">
      {label}
      <button
        onClick={onRemove}
        className="ml-0.5 rounded-full hover:bg-sd-border transition-colors p-0.5"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

interface MultiSelectChipsProps {
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (vals: string[]) => void;
  placeholder: string;
  hideChips?: boolean;
  disabled?: boolean;
}

export function MultiSelectChips({
  options,
  selected,
  onChange,
  placeholder,
  hideChips = false,
  disabled = false,
}: MultiSelectChipsProps) {
  const available = options.filter((o) => !selected.includes(o.value));

  return (
    <div className="flex flex-1 flex-col gap-1.5">
      <Select
        onValueChange={(v) => onChange([...selected, v])}
        disabled={disabled || available.length === 0}
      >
        <SelectTrigger className="h-10 rounded-lg border-sd-input bg-sd-background text-sm text-sd-foreground focus:ring-sd-ring">
          <span className={selected.length === 0 ? 'text-sd-muted-foreground' : ''}>
            {selected.length === 0 ? placeholder : `${selected.length} selected`}
          </span>
        </SelectTrigger>
        <SelectContent>
          {available.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!hideChips && selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {selected.map((v) => {
            const option = options.find((o) => o.value === v);
            return (
              <Chip
                key={v}
                label={option?.label || v}
                onRemove={() => onChange(selected.filter((s) => s !== v))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
